import { ChangeEvent } from 'react';
import { FormattedMessage } from 'react-intl';
import { Link, Route, Switch } from 'react-router-dom';
import { Box, Button, Container, Stack, Typography } from '@mui/material';

import { investorApiClient } from 'api';
import { Select } from 'components/forms';
import { CurrencyIcon } from 'components/UI';
import { useCurrencySelection, CurrencySelectionProvider } from 'providers/currency-selection';
import { LayoutProvider, useLayoutProvider } from 'providers/layout-provider';
import { MarketOverviewPage } from 'pages/MarketOverviewPage';
import { OriginatorDetailsPage } from 'pages/OriginatorDetailsPage';
import { publicMarketOverviewDataSource } from 'pages/MarketOverviewPage/marketOverviewDataSource';

const loadPublicCurrencies = async () => {
  const { data } =
    await investorApiClient.publicInvestors.publicInvestorApiControllerGetCurrencies();
  return data;
};

const PublicMarketOverviewHeader = () => {
  const { title } = useLayoutProvider();
  const { currencyOptions, currenciesLoading, selectedCurrency, setSelectedCurrency } =
    useCurrencySelection();

  const currencyOptionsWithIcons = currencyOptions.map((option) => ({
    ...option,
    label: (
      <Stack component="span" direction="row" alignItems="center" gap={1}>
        <CurrencyIcon currency={option.value} size={18} />
        <span>{option.label}</span>
      </Stack>
    ),
  }));

  return (
    <Box
      component="header"
      sx={{
        position: 'sticky',
        top: 0,
        zIndex: 10,
        borderBottom: '1px solid',
        borderColor: 'divider',
        backgroundColor: 'background.paper',
      }}
    >
      <Container
        maxWidth="xxxl"
        sx={{
          minHeight: '89px',
          display: 'flex',
          alignItems: 'center',
          gap: 3,
          py: 2,
        }}
      >
        <Typography variant="h6" fontWeight={500} flexGrow={1}>
          {title}
        </Typography>

        <Select
          name="public-market-overview-currency"
          value={selectedCurrency}
          options={currencyOptionsWithIcons}
          variant="filter"
          disableSelectedClass
          disabled={currenciesLoading || currencyOptions.length === 0}
          sx={{ width: 140 }}
          onChange={(event: ChangeEvent<HTMLInputElement>) => {
            setSelectedCurrency(event.target.value);
          }}
        />

        <Button component={Link} to="/accounts" variant="contained">
          <FormattedMessage id="public_market_overview.login" />
        </Button>
      </Container>
    </Box>
  );
};

export const PublicMarketOverviewPage = () => {
  return (
    <LayoutProvider defaultTitle={<FormattedMessage id="public_market_overview.title" />}>
      <CurrencySelectionProvider loadCurrencies={loadPublicCurrencies}>
        <PublicMarketOverviewHeader />
        <Switch>
          <Route exact path="/market-overview/:providerId">
            <OriginatorDetailsPage dataSource={publicMarketOverviewDataSource} />
          </Route>
          <Route exact path="/market-overview">
            <MarketOverviewPage dataSource={publicMarketOverviewDataSource} />
          </Route>
        </Switch>
      </CurrencySelectionProvider>
    </LayoutProvider>
  );
};
